import {pollTwoFactorChallengeMutation} from "../api/graphql/twoFactorChallenge";

// asking often is cheap: the server only reads the vendor once every 2.5s and answers the
// rest from the challenge row, so a tighter loop here costs a local round trip and nothing
// upstream. it buys back most of the pause between finishing the capture and this page
// noticing, which is otherwise the whole of what a client experiences as lag
export const LIVENESS_POLL_MS = 1500;
export const LIVENESS_FALLBACK_MS = 15 * 60 * 1000;

// a closed tab is not a verdict on its own: the service records the capture after the page
// is gone, so the wait continues this long before the attempt is called abandoned
const CLOSED_GRACE_MS = 12000;

// the window is watched far more often than the verdict is asked for, because reading
// closed is free and asking costs an upstream call
const CLOSE_CHECK_MS = 1000;

// safari only honours window.open in the synchronous part of a gesture, so the tab is
// claimed before any await and navigated once there is somewhere to send it
export const openCaptureWindow = (t) => {
    const win = window.open("", "_blank");

    if (!win) return null;

    try {
        win.document.write(
            '<!doctype html><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><body style="margin:0;padding:24px;font:14px/1.5 system-ui,-apple-system,sans-serif;color:#555"></body>',
        );
        win.document.close();
        win.document.title = t("twoFactor.livenessTitle", {defaultValue: "Face ID"});

        if (win.document.body) {
            win.document.body.textContent = t("twoFactor.livenessPreparing", {
                defaultValue: "Preparing your camera check…",
            });
        }
    } catch (error) {
        /* never fail the flow over a placeholder */
    }

    return win;
};

// the capture runs in another tab and reports to the verification service, so the only way
// back is to ask our server until it settles. a tab the client closed ends the wait early:
// otherwise the caller spins until the challenge expires, which is up to fifteen minutes
export const pollLiveness = async (challengeId, expiresAt, runRef, captureWindow) => {
    const run = ++runRef.current;
    const deadline = expiresAt
        ? new Date(expiresAt).getTime()
        : Date.now() + LIVENESS_FALLBACK_MS;

    let closedAt = null;

    const ask = async () => {
        const response = await pollTwoFactorChallengeMutation({challengeId});

        return response?.pollTwoFactorChallenge;
    };

    // holds until the next verdict is due, or breaks out early once the window has been
    // shut for the whole grace
    const waitForDue = async (until) => {
        while (Date.now() < until) {
            // cancelled, switched method, or a newer attempt started
            if (runRef.current !== run) return "superseded";

            if (captureWindow?.closed) {
                closedAt = closedAt || Date.now();

                if (Date.now() - closedAt >= CLOSED_GRACE_MS) return "abandoned";
            } else {
                closedAt = null;
            }

            await new Promise((resolve) =>
                setTimeout(resolve, Math.min(CLOSE_CHECK_MS, until - Date.now())),
            );
        }

        return "due";
    };

    while (Date.now() < deadline) {
        const waited = await waitForDue(Math.min(Date.now() + LIVENESS_POLL_MS, deadline));

        if (waited === "superseded") return null;

        const payload = await ask();

        if (runRef.current !== run) return null;

        if (payload?.__typename === "BaseError") return payload;
        if (payload?.status && payload.status !== "pending") return payload;

        // asked once more above, so a verdict recorded after the tab closed is not thrown
        // away by the grace running out a moment before it landed
        if (waited === "abandoned") return {__typename: "LivenessAbandoned"};
    }

    return {__typename: "BaseError", errKey: "admin.err.two_factor_expired"};
};
